import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * Values `NewUserForm` collects before the submit handler layers on the fields the backend also
 * requires but the user never sees (`role`, `type`, `active`, a throwaway `password` — see
 * `NewUserForm.tsx`'s `onSubmit`).
 *
 * `confirmEmail` and `parent` stay optional here even though the form always renders them:
 * that's not a rule this module is choosing to relax, it's the rule as `newUserValidationSchema`
 * already enforces it below (see the comments on both fields) — this type just describes what
 * the schema accepts.
 */
export type NewUserFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail?: string;
  phone: string;
  userCompanyType: string;
  parent?: string;
};

/**
 * Validation for the create-user form (`POST /user/new`), extracted out of `NewUserForm.tsx` so
 * the rules are testable without rendering the form (same reasoning as `itemStatus.ts` and
 * friends — see the root CLAUDE.md's Tests section).
 *
 * Carried over field-for-field from the pre-Carbon form with one correction: the original
 * `email` rule reused `validations.characters` ("The password must have at least 12
 * characters") for both the required and the malformed-address message — a copy/paste artifact,
 * not a deliberate rule, and not a validation *rule* this task was asked to preserve verbatim.
 * `validations.email`/`validations.validEmail` are the messages every other email field in this
 * app already uses (see `registerValidationSchema`, `resetPasswordValidationSchema`,
 * `newPasswordValidationSchema` in `../../utils/validations`).
 */
export const newUserValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email(i18next.t("validations.validEmail"))
    .max(255)
    .required(i18next.t("validations.email")),
  // Not `.required()`: this `oneOf` allows either a value matching `email` or a genuinely
  // `undefined` one. In practice `NewUserForm` always supplies `""` as `confirmEmail`'s default
  // (its `useForm({ defaultValues })`, matching the pre-Carbon form's own uncontrolled-input
  // default), and `""` matches neither branch — so through the actual form this already behaves
  // as required, just enforced by the mismatch rather than a `.required()` call. The `undefined`
  // branch only matters for a schema consumer other than the form (e.g. a test validating a
  // partial object directly, as below).
  confirmEmail: Yup.string().oneOf(
    [Yup.ref("email"), undefined],
    i18next.t("validations.confirmEmail")
  ),
  // Genuinely optional at the Yup layer: leaving "Responsible" unselected has never been a
  // rejected submission here (`onSubmit` falls back to the creator's own id — see
  // `NewUserForm.tsx`). Nothing under this task's brief asks that to become newly required.
  parent: Yup.string(),
  firstName: Yup.string().required(i18next.t("validations.firstName")),
  lastName: Yup.string().required(i18next.t("validations.lastName")),
  phone: Yup.string().required(i18next.t("validations.phoneNumber")),
  userCompanyType: Yup.string().required(
    i18next.t("validations.userCompanyType")
  ),
});
